// Comp/Step1Appointment.jsx
import React, { useEffect, useMemo, useState } from "react";
import Classes from "../AddAppointment.module.css";
import backend from "../../../../../../../api";

/* helpers */
function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
function labelMonth(d) {
  return d.toLocaleString(undefined, { month: "long", year: "numeric" });
}
function monthGrid(anchor) {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}
function toMin(hhmm) {
  const [h, m] = String(hhmm).split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return 0;
  return h * 60 + m;
}
function toHHMM(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
function to12hFromMin(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  const suffix = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, "0")} ${suffix}`;
}
function to12h(hhmm) {
  const [h, m] = String(hhmm).split(":").map(Number);
  return to12hFromMin(h * 60 + m);
}

/** Build 30-minute blocks from windows, filter past starts for “today” */
function buildSlots(dateISO, windows) {
  if (!dateISO || !windows?.length) return [];
  const todayISO = fmtDate(new Date());
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const out = [];
  for (const w of windows || []) {
    const s = toMin(w.start),
      e = toMin(w.end);
    for (let t = s; t + 30 <= e; t += 30) {
      if (dateISO === todayISO && t <= nowMin) continue;
      out.push(toHHMM(t));
    }
  }
  return out;
}

/** Weekly + Overrides: overrides win; otherwise fall back to weekday defaults */
function getWindowsForDate(service, dateISO) {
  if (!service || !dateISO) return [];
  const ov = (service?.dateOverrides || []).find((o) => o.date === dateISO);
  if (ov !== undefined) {
    if (ov.enabled === false) return [];
    return Array.isArray(ov.windows) ? ov.windows : [];
  }
  const weekdayIndex = new Date(dateISO).getDay();
  const weekday = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"][weekdayIndex];
  const wd = (service?.weeklySchedule || []).find((d) => d.day === weekday);
  if (!wd || !wd.enabled) return [];
  return Array.isArray(wd.windows) ? wd.windows : [];
}

export default function Step1Appointment({
  draft,
  setAppointment,
  next,
  service,
  companyId,
}) {
  const a = draft.appointment;

  const [calMonth, setCalMonth] = useState(() => new Date());
  const grid = useMemo(() => monthGrid(calMonth), [calMonth]);
  const monthLabel = useMemo(() => labelMonth(calMonth), [calMonth]);

  const windows = useMemo(
    () => getWindowsForDate(service, a.date),
    [service, a.date]
  );
  const slots = useMemo(() => buildSlots(a.date, windows), [a.date, windows]);

  // date-level availability from service schedule
  const availMap = useMemo(() => {
    const map = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (const d of grid) {
      const iso = fmtDate(d);
      const wins = getWindowsForDate(service, iso);
      const has = buildSlots(iso, wins).length > 0;
      map[iso] = d >= today && has;
    }
    return map;
  }, [grid, service]);

  // ─────────────────── NEW: slot-level availability per employee count ───────────────────
  const [slotUsage, setSlotUsage] = useState({}); // { "09:00 AM — 09:30 AM": bookedCount }
  const [employeeCount, setEmployeeCount] = useState(0);
  const [slotsLoading, setSlotsLoading] = useState(false);

  useEffect(() => {
    if (!companyId || !a.date) {
      setSlotUsage({});
      setEmployeeCount(0);
      return;
    }

    let alive = true;
    (async () => {
      try {
        setSlotsLoading(true);
        const { data } = await backend.get("/api/bookings/availability", {
          params: { companyId, date: a.date },
        });
        if (!alive) return;
        if (data?.ok) {
          setEmployeeCount(data.employeeCount || 0);
          setSlotUsage(data.slots || {});
        } else {
          setEmployeeCount(0);
          setSlotUsage({});
        }
      } catch (e) {
        console.error("availability fetch error:", e);
        if (alive) {
          setEmployeeCount(0);
          setSlotUsage({});
        }
      } finally {
        if (alive) setSlotsLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [companyId, a.date]);

  // auto-select today if available
  useEffect(() => {
    if (a.date) return;
    const todayISO = fmtDate(new Date());
    if (availMap[todayISO]) {
      setAppointment({ date: todayISO, timeSlot: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availMap, a.date]);

  // keep selection valid when availMap/slots change
  useEffect(() => {
    if (!a.date) return;
    if (!availMap[a.date]) {
      setAppointment({ date: "", timeSlot: "" });
      return;
    }
    if (a.timeSlot) {
      const valid = new Set(
        slots.map(
          (s) =>
            `${to12h(s)} — ${to12hFromMin(toMin(s) + 30)}`
        )
      );
      if (!valid.has(a.timeSlot)) setAppointment({ timeSlot: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availMap, slots]);

  const isToday = (d) => {
    const t = new Date();
    return (
      d.getFullYear() === t.getFullYear() &&
      d.getMonth() === t.getMonth() &&
      d.getDate() === t.getDate()
    );
  };
  const onDateClick = (d) => {
    const iso = fmtDate(d);
    if (!availMap[iso]) return;
    setAppointment({ date: iso, timeSlot: "" });
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!a.date) return alert("Please select a date.");
    if (!a.timeSlot) return alert("Please select a time slot.");
    next();
  };

  const calStyles = {
    card: {
      border: "1px solid #dedede",
      borderRadius: 10,
      padding: 12,
      background: "#fff",
    },
    headerRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 8,
      marginBottom: 8,
      flexWrap: "wrap",
    },
    navBtn: {
      background: "#fff",
      border: "1px solid rgba(0,0,0,0.1)",
      borderRadius: 7,
      padding: "6px 10px",
      cursor: "pointer",
      fontSize: 12,
    },
    dowRow: {
      display: "grid",
      gridTemplateColumns: "repeat(7,1fr)",
      gap: 6,
      marginBottom: 6,
      fontSize: 12,
      color: "#6b7280",
      textAlign: "center",
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(7,1fr)",
      gap: 6,
    },
    cell: {
      border: "1px solid #e5e7eb",
      borderRadius: 8,
      padding: "10px 0",
      textAlign: "center",
      cursor: "pointer",
      userSelect: "none",
      fontSize: 12,
      background: "#fff",
    },
    cellMuted: { color: "#9ca3af", background: "#fafafa" },
    cellToday: {
      outline: "2px solid var(--secondary-color)",
      outlineOffset: -2,
    },
    cellSelected: {
      background: "rgba(23,43,67,0.1)",
      borderColor: "rgba(23,43,67,0.2)",
      fontWeight: 700,
    },
    cellDisabled: {
      opacity: 0.45,
      cursor: "not-allowed",
      background: "#fafafa",
      pointerEvents: "none",
    },
    slotsWrap: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))",
      gap: 8,
      marginTop: 10,
    },
    slotBtn: {
      padding: "10px 8px",
      border: "1px solid #e5e7eb",
      borderRadius: 8,
      background: "#fff",
      cursor: "pointer",
      textAlign: "center",
      fontSize: 12,
    },
    slotActive: {
      background: "rgba(23,43,67,0.1)",
      borderColor: "rgba(23,43,67,0.2)",
      fontWeight: 700,
    },
    slotDisabled: {
      opacity: 0.5,
      cursor: "not-allowed",
      background: "#f3f4f6",
    },
    hint: { fontSize: 12, color: "#6b7280", marginTop: 6 },
  };

  return (
    <form className={Classes.infoCon} onSubmit={onSubmit}>
      <div className={Classes.infoConMiddle}>
        <h3 style={{ marginBottom: 10 }}>Appointment Details</h3>

        {/* Contact fields */}
        <div className={Classes.flexColGroup}>
          <div className={Classes.flexCol1}>
            <h5 className={Classes.label}>First Name</h5>
            <input
              className={Classes.inputField}
              name="firstName"
              value={a.firstName}
              onChange={(e) => setAppointment({ firstName: e.target.value })}
              placeholder="First Name"
              required
            />
          </div>
          <div className={Classes.flexCol1}>
            <h5 className={Classes.label}>Last Name</h5>
            <input
              className={Classes.inputField}
              name="lastName"
              value={a.lastName}
              onChange={(e) => setAppointment({ lastName: e.target.value })}
              placeholder="Last Name"
              required
            />
          </div>
        </div>

        <div className={Classes.flexColGroup}>
          <div className={Classes.flexCol1}>
            <h5 className={Classes.label}>Email</h5>
            <input
              type="email"
              className={Classes.inputField}
              name="email"
              value={a.email}
              onChange={(e) => setAppointment({ email: e.target.value })}
              placeholder="Email"
              required
            />
          </div>
          <div className={Classes.flexCol1}>
            <h5 className={Classes.label}>Phone</h5>
            <input
              className={Classes.inputField}
              name="phone"
              value={a.phone}
              onChange={(e) => setAppointment({ phone: e.target.value })}
              placeholder="+1 (000) 000-0000"
              required
            />
          </div>
        </div>

        <div className={Classes.flexColGroup}>
          <div className={Classes.flexCol1}>
            <div className={Classes.flexAlign10}>
              <h5 className={Classes.label}>Company</h5>
              <span className={"optionalText"}>
                <h5>Optional</h5>
              </span>
            </div>
            <input
              className={Classes.inputField}
              name="company"
              value={a.company}
              onChange={(e) => setAppointment({ company: e.target.value })}
              placeholder="Company (Optional)"
            />
          </div>
        </div>

        <div className={Classes.flexColGroup}>
          <div className={Classes.flexCol1}>
            <div className={Classes.textAreaCon}>
              <textarea
                rows={4}
                name="notes"
                placeholder="Anything Else We Should Know? (Optional)"
                value={a.notes}
                onChange={(e) => setAppointment({ notes: e.target.value })}
              />
            </div>
          </div>
        </div>

        {/* Calendar + time slot selection */}
        <h3 style={{ margin: "10px 0" }}>Select Date &amp; Time</h3>
        <div className={Classes.flexColGroup}>
          <div className={Classes.flexCol1}>
            <div style={calStyles.card}>
              <div style={calStyles.headerRow}>
                <button
                  type="button"
                  style={calStyles.navBtn}
                  onClick={() =>
                    setCalMonth(
                      (m) =>
                        new Date(m.getFullYear(), m.getMonth() - 1, 1)
                    )
                  }
                >
                  ‹ Prev
                </button>
                <div
                  className={Classes.title3}
                  style={{ fontSize: 16 }}
                >
                  {monthLabel}
                </div>
                <button
                  type="button"
                  style={calStyles.navBtn}
                  onClick={() =>
                    setCalMonth(
                      (m) =>
                        new Date(m.getFullYear(), m.getMonth() + 1, 1)
                    )
                  }
                >
                  Next ›
                </button>
              </div>

              <div style={calStyles.dowRow}>
                <div>Sun</div>
                <div>Mon</div>
                <div>Tue</div>
                <div>Wed</div>
                <div>Thu</div>
                <div>Fri</div>
                <div>Sat</div>
              </div>

              <div style={calStyles.grid}>
                {grid.map((d, i) => {
                  const iso = fmtDate(d);
                  const selected = a.date === iso;
                  const isAvail = !!availMap[iso];
                  const style = {
                    ...calStyles.cell,
                    ...(d.getMonth() === calMonth.getMonth()
                      ? {}
                      : calStyles.cellMuted),
                    ...(isToday(d) ? calStyles.cellToday : {}),
                    ...(selected ? calStyles.cellSelected : {}),
                    ...(!isAvail ? calStyles.cellDisabled : {}),
                  };
                  return (
                    <div
                      key={iso + i}
                      style={style}
                      aria-disabled={!isAvail}
                      onClick={() => isAvail && onDateClick(d)}
                    >
                      {d.getDate()}
                    </div>
                  );
                })}
              </div>

              <div style={calStyles.hint}>
                Dates inherit the Weekly Availability unless a per-date override
                exists. Only dates with available time windows are clickable.
              </div>
            </div>
          </div>

          {/* Slots */}
          <div className={Classes.flexCol1}>
            <h5 className={Classes.label}>Available 30 minutes Slots</h5>
            {slotsLoading && (
              <div
                className={Classes.text}
                style={{ margin: "4px 0 8px" }}
              >
                Checking employee availability…
              </div>
            )}
            {a.date && !slots.length && (
              <div
                className={Classes.text}
                style={{ margin: "8px 0" }}
              >
                No slots available for the selected date.
              </div>
            )}
            <div style={calStyles.slotsWrap}>
              {slots.map((s) => {
                const label = `${to12h(s)} — ${to12hFromMin(
                  toMin(s) + 30
                )}`;
                const active = a.timeSlot === label;

                const booked = slotUsage[label] || 0;
                const isFull =
                  employeeCount > 0 && booked >= employeeCount;

                const style = {
                  ...calStyles.slotBtn,
                  ...(active ? calStyles.slotActive : {}),
                  ...(isFull ? calStyles.slotDisabled : {}),
                };

                return (
                  <button
                    key={s}
                    type="button"
                    disabled={isFull}
                    onClick={() =>
                      !isFull && setAppointment({ timeSlot: label })
                    }
                    style={style}
                    title={
                      isFull
                        ? "All employees are already booked in this time slot"
                        : ""
                    }
                  >
                    {label}
                    {isFull && (
                      <div
                        style={{
                          fontSize: 10,
                          marginTop: 4,
                          color: "#9ca3af",
                        }}
                      >
                        Fully booked
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className={Classes.navRow}>
        <button
          type="button"
          className={Classes.backBtn}
          disabled
        >
          <h5>Back</h5>
        </button>
        <button type="submit" className={Classes.saveBtn}>
          <h5>Next</h5>
        </button>
      </div>
    </form>
  );
}
